import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import FamilyStudentExpense from "./FamilyStudentExpense";
import PaymentHistoryStatistics from "./SoloPaymentHistoryStatistics";
import SoloPaymentStatistics from "./SoloPaymentStatistics";
import FamilyPaymentHistory from "./FamilyPaymentHistory";

import {
  FaSearch,
  FaUser,
  FaIdCard,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaLock,
  FaLockOpen,
  FaCheckCircle,
  FaEye,
  FaHistory,
  FaRupeeSign,
  FaFilter,
  FaTimes,
  FaPaperPlane,
  FaCreditCard,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSync,
  FaTrash,
  FaEdit,
  FaFileInvoiceDollar,
  FaRegCalendarCheck,
  FaRegCalendarTimes,
  FaChartLine,
  FaUsers,
  FaArrowRight,
  FaBan,
  FaTools,
  FaCog,
  FaPercent,
  FaCalendarCheck,
  FaCalendarDay,
  FaDatabase,
  FaUserFriends,
  FaHome,
  FaPhone,
  FaMapMarkerAlt,
  FaUserPlus,
  FaClock,
  FaExclamationCircle,
  FaListAlt,
  FaReceipt,
} from "react-icons/fa";
import {
  FiSearch,
  FiUser,
  FiUsers,
  FiHome,
  FiCreditCard,
  FiPhone,
  FiMail,
  FiCalendar,
  FiBook,
  FiPlus,
  FiSave,
  FiCheck,
  FiArrowRight,
  FiChevronRight,
  FiChevronLeft,
  FiRefreshCw,
  FiX,
  FiEdit,
  FiAlertCircle,
} from "react-icons/fi";
import "./PayFamStdFee.css";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// API URLs
const API_BASE_URL_For_Fam_Code = "https://mhsdev.online/MHS-WEB/api";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php";

// API Configuration
const API_CONFIG = {
  timeout: 30000,
  retries: 2,
  retryDelay: 1000,
};

const currentYear = moment().year();
const years = [currentYear - 1, currentYear, currentYear + 1];

const PayFamilyFee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState("fee");

  // State for Family Code
  const [familyCode, setFamilyCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [familyCodeSuggestions, setFamilyCodeSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Family Data State
  const [familyData, setFamilyData] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [classFees, setClassFees] = useState({});
  const [isLoadingClassFees, setIsLoadingClassFees] = useState(false);

  // Family Financial Status
  const [familyFinancialStatus, setFamilyFinancialStatus] = useState(null);
  const [initialSetupRecords, setInitialSetupRecords] = useState([]);
  const [activeLedgerRecords, setActiveLedgerRecords] = useState({
    monthly: [],
    expense: [],
  });

  // ======================================================================
  // 🎯 SINGLE SOURCE OF TRUTH – EFFECTIVE MONTHLY FEE
  // ======================================================================
  const [effectiveMonthlyFee, setEffectiveMonthlyFee] = useState(0);
  const [effectiveFeeSource, setEffectiveFeeSource] = useState("calculated"); // 'custom_fee', 'calculated', 'fallback'

  // Financial Incomplete Warning State
  const [financialIncompleteWarning, setFinancialIncompleteWarning] = useState({
    show: false,
    message: "",
    details: "",
  });

  // Fee Setup Validation State
  const [isFeeSetupIncomplete, setIsFeeSetupIncomplete] = useState(false);
  const [feeSetupValidationData, setFeeSetupValidationData] = useState(null);

  // Fee Section State
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [feeRecords, setFeeRecords] = useState([]);
  const [feeFormData, setFeeFormData] = useState({
    feePaid: "",
    feePending: "0",
    totalFee: "0",
    discountType: "percentage",
    discountValue: "0",
    paymentMode: "cash",
    transactionId: "",
    remarks: "",
  });

  // Payment History Statistics State
  const [paymentHistoryData, setPaymentHistoryData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Database Connection State
  const [isDbConnected, setIsDbConnected] = useState(true);
  const [dbStats, setDbStats] = useState(null);
  const [isLoadingDb, setIsLoadingDb] = useState(false);

  // ======================================================================
  // CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  // ======================================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // No dropdown to close currently
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load family code from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const storedCode = searchParams.get("FC");
    if (storedCode) {
      setFamilyCode(storedCode);
    }
  }, [location]);

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // ======================================================================
  // API CALL HELPER (FULL, UNCHANGED – COPIED FROM YOUR ORIGINAL FILE)
  // ======================================================================
  const callAPI = async (
    action,
    data = null,
    method = "POST",
    retries = API_CONFIG.retries
  ) => {
    let lastError = null;
    let lastResponse = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const url = new URL(API_BASE_URL);
        url.searchParams.append("action", action);

        const config = {
          method: method.toUpperCase(),
          url: url.toString(),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: API_CONFIG.timeout,
          validateStatus: function (status) {
            return true;
          },
        };

        if (method.toUpperCase() === "GET" && data) {
          Object.keys(data).forEach((key) => {
            if (
              data[key] !== null &&
              data[key] !== undefined &&
              data[key] !== ""
            ) {
              url.searchParams.append(key, data[key]);
            }
          });
          config.url = url.toString();
        } else if (
          data &&
          (method.toUpperCase() === "POST" || method.toUpperCase() === "PUT")
        ) {
          config.data = data;
        }

        const response = await axios(config);
        lastResponse = response.data;

        if (response.status >= 400) {
          let serverErrorMessage = "Unknown server error";

          if (response.data) {
            if (typeof response.data === "string") {
              serverErrorMessage = response.data;
            } else if (response.data.error) {
              serverErrorMessage = response.data.error;
            } else if (response.data.message) {
              serverErrorMessage = response.data.message;
            } else {
              serverErrorMessage = JSON.stringify(response.data);
            }
          }

          throw {
            message: serverErrorMessage,
            response: response.data,
            status: response.status,
            statusText: response.statusText,
            isHttpError: true,
            isApiError: true,
          };
        }

        if (response.data && response.data.error) {
          throw {
            message: response.data.error,
            response: response.data,
            status: response.status,
            isHttpError: false,
            isApiError: true,
            isResponseError: true,
          };
        }

        if (!response.data) {
          throw {
            message: "Empty response from server",
            status: response.status,
            isHttpError: false,
            isApiError: true,
            isEmptyResponse: true,
          };
        }

        return response.data;
      } catch (error) {
        lastError = error;

        if (attempt < retries) {
          const delay = API_CONFIG.retryDelay * (attempt + 1);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        if (error.response) {
          const serverError =
            error.response.data?.error ||
            error.response.data?.message ||
            error.message ||
            `Server Error: ${error.response.status} ${error.response.statusText}`;

          return {
            error: serverError,
            message: serverError,
            _httpStatus: error.response.status,
            _statusText: error.response.statusText,
            _isHttpError: true,
            _isApiError: true,
            _isServerError: true,
            _retriesAttempted: attempt,
          };
        } else if (error.request) {
          return {
            error: "No response from server",
            message:
              "No response from server. Please check your network connection and try again.",
            status: 0,
            _isNetworkError: true,
            _isHttpError: false,
            _isApiError: true,
            _retriesAttempted: attempt,
          };
        } else if (error.isApiError) {
          return {
            error: error.message,
            message: error.message,
            _isHttpError: false,
            _isApiError: true,
            _isResponseError: true,
            _retriesAttempted: attempt,
          };
        } else if (error.code === "ECONNABORTED") {
          return {
            error: "Request timeout",
            message: "Server took too long to respond. Please try again.",
            _isTimeoutError: true,
            _isNetworkError: true,
            _isHttpError: false,
            _isApiError: true,
            _retriesAttempted: attempt,
          };
        } else {
          return {
            error: error.message || "Unknown error occurred",
            message: error.message || "An unknown error occurred",
            _isUnknownError: true,
            _isHttpError: false,
            _isApiError: true,
            _retriesAttempted: attempt,
          };
        }
      }
    }

    return {
      error: "Unexpected error in API call",
      message: "An unexpected error occurred while making the API call",
      _isUnexpectedError: true,
      _retriesAttempted: retries,
    };
  };

  const api = {
    autocompleteFamilyCode: (query) =>
      callAPI("autocomplete-family-code", { query }, "GET"),
    getFamilyFinancialStatus: (familyCode) =>
      callAPI(
        "get-family-financial-status",
        { family_code: familyCode },
        "POST"
      ),
  };

  // ======================================================================
  // FETCH AUTOCOMPLETE SUGGESTIONS (UNCHANGED)
  // ======================================================================
  const fetchFamilyCodeSuggestions = async (query) => {
    if (query.length < 2) {
      setFamilyCodeSuggestions([]);
      return;
    }

    try {
      const response = await api.autocompleteFamilyCode(query);

      if (response && response.success && response.suggestions) {
        const suggestions = response.suggestions.map((code) => ({
          family_code: code,
          family_name: `Family ${code}`,
        }));
        setFamilyCodeSuggestions(suggestions);
      } else {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL_For_Fam_Code}/students.php?action=search-family`,
          { params: { type: "family-code", query: query } }
        );
        if (fallbackResponse.data.results) {
          setFamilyCodeSuggestions(fallbackResponse.data.results);
        }
      }
    } catch (error) {
      console.error("Error fetching family code suggestions:", error);
      try {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL_For_Fam_Code}/students.php?action=search-family`,
          { params: { type: "family-code", query: query } }
        );
        if (fallbackResponse.data.results) {
          setFamilyCodeSuggestions(fallbackResponse.data.results);
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  };

  const handleFamilyCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFamilyCode(value);
    fetchFamilyCodeSuggestions(value);
  };

  // ======================================================================
  // FETCH CLASS FEES (UNCHANGED)
  // ======================================================================
  const fetchClassFees = async () => {
    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=get-class-fees",
        { timeout: 5000 }
      );
      if (response.data.success) {
        return response.data.class_fees || {};
      } else {
        console.error("Failed to fetch class fees:", response.data.error);
        return {};
      }
    } catch (error) {
      console.error("Error fetching class fees:", error);
      return {};
    }
  };

  const getClassMonthlyFee = (className, fees = classFees) => {
    if (!className || !fees[className]) return 0;
    return parseFloat(fees[className].monthly_fee) || 0;
  };

  // ======================================================================
  // 🧮 SIMPLIFIED MONTH HELPERS (USE effectiveMonthlyFee)
  // ======================================================================
 // ======================================================================
  // 🧮 UPDATED MONTH HELPERS (Respects Historical/Partial Status)
  // ======================================================================
  
  // Helper to find the record for a specific month
  const getMonthRecord = (month) => {
    if (!activeLedgerRecords?.monthly) return null;
    // Find the latest transaction for this month
    return activeLedgerRecords.monthly.find((r) => r.paid_month === month);
  };

  const getMonthAmount = (month) => {
    if (familyFinancialStatus !== "old_student") return 0;
    
    const record = getMonthRecord(month);

    // 1. If we have a record, check its specific status
    if (record) {
      // Check the new payment_status column or fallback to fstatus
      const status = (record.payment_status || record.fstatus || "").toLowerCase();
      
      // ✅ IF PAID: Trust the history. Pending is 0.
      if (status === 'paid') {
        return 0;
      }

      // ⚠️ IF PARTIAL: Use the effective fee sent by PHP for THIS record
      // The PHP now sends 'effective_monthly_fee' which is either the old fee (if paid)
      // or the new fee (if partial/unpaid).
      const targetFee = parseFloat(record.effective_monthly_fee || effectiveMonthlyFee);
      const paid = parseFloat(record.fee_paid || 0);
      
      return Math.max(0, targetFee - paid);
    }

    // 2. If no record exists yet, use the current global effective fee
    return effectiveMonthlyFee;
  };

  const getMonthStatus = (month) => {
    if (familyFinancialStatus !== "old_student") return "unavailable";

    const record = getMonthRecord(month);

    // 1. If record exists, verify status
    if (record) {
       const status = (record.payment_status || record.fstatus || "").toLowerCase();
       if (status === 'paid') return 'paid';
       
       // Recalculate partial status based on math to be safe
       const pending = getMonthAmount(month);
       if (pending <= 0) return 'paid';
       return 'partial';
    }

    // 2. If no record, it's unpaid
    return "unpaid";
  };

  const isMonthFullyPaid = (month) => {
    return getMonthStatus(month) === 'paid';
  };

  const calculateTotalPendingFee = () => {
    if (familyFinancialStatus !== "old_student") return 0;
    let totalPending = 0;
    months.forEach((month) => {
      totalPending += getMonthAmount(month);
    });
    return totalPending;
  };

  const calculateTotalMonthlyFee = () => effectiveMonthlyFee;

  const getCurrentMonthStatusDetails = () => {
    if (familyFinancialStatus !== "old_student") {
      return { status: "unavailable", amount: 0, paid: 0, pending: 0 };
    }
    const currentMonth = moment().format("MMMM");
    
    // Use the new helper to get accurate math
    const pending = getMonthAmount(currentMonth);
    const record = getMonthRecord(currentMonth);
    
    // Determine target amount (Historic vs Current)
    const targetAmount = record ? parseFloat(record.effective_monthly_fee || effectiveMonthlyFee) : effectiveMonthlyFee;
    const paid = record ? parseFloat(record.fee_paid || 0) : 0;

    return {
      status: getMonthStatus(currentMonth),
      amount: targetAmount,
      paid: paid,
      pending: pending,
    };
  };

  // ======================================================================
  // CALCULATE TOTALS FROM ACTIVE LEDGER (UNCHANGED)
  // ======================================================================
  const calculateTotalsFromActiveLedger = () => {
    if (
      !activeLedgerRecords.monthly ||
      activeLedgerRecords.monthly.length === 0
    ) {
      return {
        totalMonthlyFee: 0,
        totalFeePaid: 0,
        totalFeePending: 0,
        totalExpensePayable: 0,
        totalExpensePaid: 0,
        totalExpensePending: 0,
      };
    }

    let totals = {
      totalMonthlyFee: effectiveMonthlyFee,
      totalFeePaid: 0,
      totalFeePending: 0,
      totalExpensePayable: 0,
      totalExpensePaid: 0,
      totalExpensePending: 0,
    };

    activeLedgerRecords.monthly.forEach((record) => {
      totals.totalFeePaid += parseFloat(record.fee_paid) || 0;
      totals.totalFeePending += parseFloat(record.fee_pending) || 0;
    });

    activeLedgerRecords.expense.forEach((record) => {
      totals.totalExpensePayable += parseFloat(record.expense_payable) || 0;
      totals.totalExpensePaid += parseFloat(record.expense_paid) || 0;
      totals.totalExpensePending += parseFloat(record.expense_pending) || 0;
    });

    totals.totalAmount = totals.totalMonthlyFee + totals.totalExpensePayable;
    totals.totalPaid = totals.totalFeePaid + totals.totalExpensePaid;
    totals.totalPending = totals.totalFeePending + totals.totalExpensePending;

    return totals;
  };

  // ======================================================================
  // CALCULATE TOTALS FROM INITIAL SETUP (UNCHANGED)
  // ======================================================================
  const calculateTotalsFromInitialSetup = () => {
    if (!initialSetupRecords || initialSetupRecords.length === 0) {
      return {
        totalMonthlyFee: 0,
        totalPaid: 0,
        totalPending: 0,
        coverage: "0/0",
      };
    }

    let totals = {
      totalMonthlyFee: 0,
      totalPaid: 0,
      totalPending: 0,
      totalStudents: initialSetupRecords.length,
      coveredStudents: initialSetupRecords.length,
      totalStudentsInFamily: familyMembers.length,
    };

    initialSetupRecords.forEach((record) => {
      totals.totalMonthlyFee += parseFloat(record.monthly_fee) || 0;
      totals.totalPaid += parseFloat(record.total_paid) || 0;
      totals.totalPending += parseFloat(record.last_pendings) || 0;
    });

    totals.coverage = `${totals.coveredStudents}/${totals.totalStudentsInFamily}`;

    return totals;
  };

  // ======================================================================
  // HANDLE SUGGESTION CLICK
  // ======================================================================
  const handleSuggestionClick = (suggestion, type) => {
    if (type === "familyCode") {
      setFamilyCode(suggestion.family_code);
      setFamilyCodeSuggestions([]);
    }
  };

  // ======================================================================
  // DATABASE CONNECTION TEST (UNCHANGED)
  // ======================================================================
  const checkDatabaseConnection = async () => {
    setIsLoadingDb(true);
    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?test=true",
        { timeout: 5000 }
      );

      if (response.data.test_status === "OK" || response.data.success) {
        setIsDbConnected(true);
        return true;
      } else {
        setIsDbConnected(false);
        return false;
      }
    } catch (error) {
      console.error("❌ Database connection test failed:", error);
      setIsDbConnected(false);
      return false;
    } finally {
      setIsLoadingDb(false);
    }
  };

  // ======================================================================
  // FETCH PAYMENT HISTORY (UNCHANGED)
  // ======================================================================
  const fetchPaymentHistoryData = async () => {
    if (!familyCode) return;
    setIsLoadingHistory(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=get-family-payment-history",
        {
          family_code: familyCode,
          filter: "all",
          page: 1,
          limit: 20,
        },
        { timeout: 10000 }
      );

      if (response.data.success) {
        setPaymentHistoryData(response.data.data || []);
      } else {
        console.error("Failed to fetch payment history:", response.data.error);
        setPaymentHistoryData([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch payment history:", error);
      setPaymentHistoryData([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // ======================================================================
  // FETCH STATISTICS (UNCHANGED)
  // ======================================================================
  const fetchStatsData = async () => {
    if (!familyCode) return;
    setIsLoadingStats(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=get-family-statistics-data",
        {
          family_code: familyCode,
          year: new Date().getFullYear(),
        },
        { timeout: 10000 }
      );

      if (response.data.success) {
        setStatsData(response.data);
      } else {
        console.error("Failed to fetch statistics:", response.data.error);
        setStatsData(null);
      }
    } catch (error) {
      console.error("❌ Failed to fetch statistics:", error);
      setStatsData(null);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // ======================================================================
  // HANDLE REFRESH HISTORY
  // ======================================================================
  const handleRefreshHistory = () => {
    if (activeTab === "history" && familyFinancialStatus === "old_student") {
      fetchPaymentHistoryData();
    } else if (
      activeTab === "stats" &&
      familyFinancialStatus === "old_student"
    ) {
      fetchStatsData();
    }
  };

  // ======================================================================
  // FEE FORM HELPERS (UNCHANGED EXCEPT USING SIMPLIFIED HELPERS)
  // ======================================================================
  const getMonthIcon = (month) => {
    const status = getMonthStatus(month);
    switch (status) {
      case "paid":
        return <FaCheckCircle className="payFamilyFee-monthIcon" />;
      case "partial":
        return <FaLockOpen className="payFamilyFee-monthIcon" />;
      case "unpaid":
        return <FaLock className="payFamilyFee-monthIcon" />;
      default:
        return <FaInfoCircle className="payFamilyFee-monthIcon" />;
    }
  };

  const getCurrentMonthStatusIcon = () => {
    const status = getCurrentMonthStatusDetails().status;
    switch (status) {
      case "paid":
        return <FaCheckCircle className="payFamilyFee-currentMonthIcon paid" />;
      case "partial":
        return <FaLockOpen className="payFamilyFee-currentMonthIcon partial" />;
      case "unpaid":
        return <FaLock className="payFamilyFee-currentMonthIcon unpaid" />;
      default:
        return <FaInfoCircle className="payFamilyFee-currentMonthIcon" />;
    }
  };

  const handleCheckboxChange = (month) => {
    setSelectedMonths((prevSelected) =>
      prevSelected.includes(month)
        ? prevSelected.filter((m) => m !== month)
        : [...prevSelected, month]
    );
  };

  const calculateTotalFee = () => {
    if (familyFinancialStatus !== "old_student") return 0;
    return selectedMonths.reduce((sum, month) => sum + getMonthAmount(month), 0);
  };

  const getMonthPaymentSummary = (month) => {
    const monthRecords = activeLedgerRecords.monthly.filter(
      (r) => r.paid_month === month
    );
    let totalPaid = monthRecords.reduce(
      (sum, r) => sum + (parseFloat(r.fee_paid) || 0),
      0
    );
    const remaining = getMonthAmount(month);
    const isFullyPaid = remaining <= 0;
    return {
      totalPaid,
      remaining,
      paymentCount: monthRecords.length,
      monthlyFee: effectiveMonthlyFee,
      isFullyPaid,
      isPartiallyPaid: totalPaid > 0 && !isFullyPaid,
      records: monthRecords,
    };
  };

  const calculateDiscountedFee = () => {
    const totalFee = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;

    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (totalFee * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
    discountAmount = Math.min(discountAmount, totalFee);
    return totalFee - discountAmount;
  };

  const calculateFeeDiscountAmount = () => {
    const totalFee = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;
    if (discountType === "percentage") {
      return (totalFee * discountValue) / 100;
    } else {
      return Math.min(discountValue, totalFee);
    }
  };

  const handleFeePaidChange = (e) => {
    const feePaidValue = parseFloat(e.target.value) || 0;
    const discountedTotal = calculateDiscountedFee();

    let pendingAmount = discountedTotal - feePaidValue;

    if (selectedMonths.length > 1) {
      pendingAmount = 0;
    }

    setFeeFormData((prev) => ({
      ...prev,
      feePaid: e.target.value,
      feePending: pendingAmount >= 0 ? pendingAmount.toString() : "0",
    }));
  };

  const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ======================================================================
  // 🆕 MONTH CARD DISPLAY – CLEAN, NO STRIKETHROUGH, NO "!" BADGE
  // ======================================================================
  const renderMonthFeeDisplay = (month) => {
    const amount = getMonthAmount(month);
    const status = getMonthStatus(month);

    if (status === "paid") {
      return (
        <div className="payFamilyFee-paidStatus">
          <span style={{ color: "#27ae60", display: "flex", alignItems: "center", gap: "4px" }}>
            <FaCheckCircle style={{ fontSize: "12px" }} />
            PAID
          </span>
        </div>
      );
    }

    return (
      <span className="payFamilyFee-monthAmountValue">
        RS. {amount.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
      </span>
    );
  };

  // ======================================================================
  // 🚀 UPDATED handleSearch – RECEIVES effectiveMonthlyFee DIRECTLY
  // ======================================================================
  const handleSearch = async () => {
    if (!familyCode.trim()) {
      Swal.fire({
        title: "Family Code Required",
        text: "Please enter a Family Code",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          popup: "swal-wide",
        },
      });
      return;
    }

    setIsLoading(true);
    setFamilyFinancialStatus(null);
    setInitialSetupRecords([]);
    setActiveLedgerRecords({ monthly: [], expense: [] });
    setIsFeeSetupIncomplete(false);
    setFeeSetupValidationData(null);
    setPaymentHistoryData(null);
    setStatsData(null);
    setFinancialIncompleteWarning({ show: false, message: "", details: "" });
    setEffectiveMonthlyFee(0);
    setEffectiveFeeSource("calculated");

    try {
      Swal.fire({
        title: "Analyzing Family Financial Status...",
        text: "Please wait while we fetch and analyze family financial data.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Fetch class fees if not already loaded
      let classFeesData = classFees;
      if (Object.keys(classFees).length === 0) {
        try {
          classFeesData = await fetchClassFees();
          setClassFees(classFeesData);
        } catch (error) {
          console.warn("Could not fetch class fees, proceeding without them:", error);
        }
      }

      // Fetch family financial status
      const response = await api.getFamilyFinancialStatus(familyCode);

      if (response.success) {
        const {
          status,
          message,
          family_info,
          monthly_records = [],
          expense_records = [],
          initial_setup_records = [],
          summary,
          note,
          action_required,
          effective_monthly_fee,
          fee_source,
        } = response;

        // ─── SET THE EFFECTIVE MONTHLY FEE (SINGLE SOURCE OF TRUTH) ───
        setEffectiveMonthlyFee(effective_monthly_fee || 0);
        setEffectiveFeeSource(fee_source || "calculated");

        // Process students
        let processedStudents = [...(family_info.students || [])];

        const activeStudents = processedStudents.filter(
          (student) => student.active_status === "active" || student.active_status === "Active"
        );
        const inactiveStudents = processedStudents.filter(
          (student) => student.active_status === "inactive" || student.active_status === "Inactive"
        );

        // Fill missing monthly_fee for inactive students from class fees (display only)
        if (inactiveStudents.length > 0 && Object.keys(classFeesData).length > 0) {
          processedStudents = processedStudents.map((student) => {
            const isInactive =
              student.active_status === "inactive" || student.active_status === "Inactive";
            const hasMonthlyFee = student.monthly_fee && parseFloat(student.monthly_fee) > 0;
            if (isInactive && !hasMonthlyFee && student.class) {
              const classFee = getClassMonthlyFee(student.class, classFeesData);
              return {
                ...student,
                monthly_fee: classFee,
                monthly_fee_source: "class_fee_table",
              };
            }
            return student;
          });
        }

        const finalActiveStudents = processedStudents.filter(
          (s) => s.active_status === "active" || s.active_status === "Active"
        );
        const finalInactiveStudents = processedStudents.filter(
          (s) => s.active_status === "inactive" || s.active_status === "Inactive"
        );

        setFamilyData({
          familyCode: family_info.family_code,
          familyName: family_info.family_name || `${family_info.father_name || ""} Family`,
          totalStudents: family_info.total_students,
          address: family_info.address || "",
          mobile: family_info.mobile || "",
          activeStudentsCount: finalActiveStudents.length,
          inactiveStudentsCount: finalInactiveStudents.length,
          hasInactiveStudents: finalInactiveStudents.length > 0,
          inactiveFeesTotal: finalInactiveStudents.reduce(
            (sum, s) => sum + (parseFloat(s.monthly_fee) || 0),
            0
          ),
        });

        setFamilyMembers(processedStudents);
        setFamilyFinancialStatus(status);
        Swal.close();

        switch (status) {
          case "old_student":
            setActiveLedgerRecords({
              monthly: monthly_records || [],
              expense: expense_records || [],
            });
            setFeeRecords(monthly_records || []);
            fetchPaymentHistoryData();
            fetchStatsData();

            Swal.fire({
              title: "✅ Active Family Found!",
              html: `
                <div class="payFamilyFee-statusModal">
                  <h3>Family Status: <span class="status-old">Active/Old Student</span></h3>
                  <p>${message}</p>
                  <div class="payFamilyFee-statusDetails">
                    <p><strong>Note:</strong> ${note}</p>
                  </div>
                </div>
              `,
              icon: "success",
              confirmButtonText: "Proceed with Fee Payment",
              customClass: { popup: "swal-wide" },
            });
            break;

          case "new_student_with_setup":
            setInitialSetupRecords(initial_setup_records || []);
            setFeeSetupValidationData(response);
            Swal.fire({
              title: "⚠️ New Family Detected",
              html: `
                <div class="payFamilyFee-statusModal">
                  <h3>Family Status: <span class="status-new">New Student (Initial Setup)</span></h3>
                  <p>${message}</p>
                  <p><strong>Note:</strong> ${note}</p>
                </div>
              `,
              icon: "info",
              confirmButtonText: "Continue to First Payment",
              showCancelButton: true,
              cancelButtonText: "Review Setup",
              customClass: { popup: "swal-wide" },
            }).then((result) => {
              if (result.isConfirmed) {
                setActiveTab("fee");
              } else {
                showInitialSetupDetails();
              }
            });
            break;

          case "financial_limbo":
            setIsFeeSetupIncomplete(true);
            setFeeSetupValidationData(response);
            Swal.fire({
              title: "❌ Financial Setup Incomplete",
              html: `
                <div class="payFamilyFee-statusModal">
                  <h3>Family Status: <span class="status-limbo">Financial Limbo</span></h3>
                  <p>${message}</p>
                  <p><strong>Action Required:</strong> ${action_required}</p>
                </div>
              `,
              icon: "error",
              confirmButtonText: "Go to Fee Setup",
              showCancelButton: true,
              cancelButtonText: "Cancel",
              customClass: { popup: "swal-wide" },
            }).then((result) => {
              if (result.isConfirmed) {
                navigate("/fee-setup?family_code=" + familyCode);
              }
            });
            setIsLoading(false);
            return;

          default:
            Swal.fire({
              title: "Unknown Status",
              text: "Unexpected family status received",
              icon: "warning",
              confirmButtonText: "OK",
            });
            setIsLoading(false);
            return;
        }

        // Reset fee form
        setFeeFormData({
          feePaid: "",
          feePending: "0",
          totalFee: "0",
          discountType: "percentage",
          discountValue: "0",
          paymentMode: "cash",
          transactionId: "",
          remarks: "",
        });

        setSelectedMonths([]);
        checkDatabaseConnection();
      } else {
        Swal.close();

        if (response.status === "not_found") {
          throw new Error("Family Code not found. Please verify the code.");
        } else if (response.status === "financial_limbo") {
          return;
        } else {
          throw new Error(response.message || "Failed to fetch family details");
        }
      }
    } catch (error) {
      console.error("❌ Fetch Error:", error);
      Swal.close();

      let errorMessage = "An error occurred while searching for the family.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (
        errorMessage.includes("financial records are incomplete") ||
        errorMessage.includes("Family found but financial records are incomplete")
      ) {
        setFinancialIncompleteWarning({
          show: true,
          message: "Family found but financial records are incomplete.",
          details:
            "This family exists in the system but the financial setup is not complete. Please complete the financial setup to proceed with payments.",
        });
        setFamilyData({
          familyCode: familyCode,
          familyName: "Family " + familyCode,
          totalStudents: 0,
          address: "",
          mobile: "",
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: "swal-wide" },
        });
        setFamilyData(null);
        setFamilyMembers([]);
        setFeeRecords([]);
        setFamilyFinancialStatus(null);
        setInitialSetupRecords([]);
        setActiveLedgerRecords({ monthly: [], expense: [] });
        setIsFeeSetupIncomplete(false);
        setFeeSetupValidationData(null);
        setPaymentHistoryData(null);
        setStatsData(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ======================================================================
  // FEE SUBMISSION – FULL, UNCHANGED (uses getMonthAmount etc.)
  // ======================================================================
  const handleFeeSubmit = async (event) => {
    event.preventDefault();

    if (selectedMonths.length === 0) {
      Swal.fire({
        title: "No Month Selected",
        text: "Please select at least one month",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    const feePaidValue = parseFloat(feeFormData.feePaid) || 0;
    const calculatedTotal = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;

    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (calculatedTotal * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
    discountAmount = Math.min(discountAmount, calculatedTotal);
    const discountedTotal = calculatedTotal - discountAmount;

    if (feePaidValue <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid fee amount",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    if (selectedMonths.length === 1) {
      const month = selectedMonths[0];
      const monthAmount = getMonthAmount(month);
      if (feePaidValue > monthAmount + 0.01) {
        Swal.fire({
          title: "Amount Exceeded",
          html: `
            <div class="validation-error">
              <h3>Payment Amount Error</h3>
              <p>Payment amount cannot exceed the remaining amount for this month.</p>
              <div class="validation-details">
                <div><strong>Remaining Amount:</strong> ${formatCurrency(monthAmount)}</div>
                <div><strong>Amount Paid:</strong> ${formatCurrency(feePaidValue)}</div>
                <div><strong>Excess:</strong> ${formatCurrency(feePaidValue - monthAmount)}</div>
              </div>
              <p class="validation-note">You can pay up to ${formatCurrency(monthAmount)} for this month.</p>
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: "swal-wide" },
        });
        return;
      }
    } else if (selectedMonths.length > 1) {
      if (Math.abs(feePaidValue - discountedTotal) > 0.01) {
        Swal.fire({
          title: "Payment Amount Error",
          html: `
            <div class="validation-error">
              <h3>Multiple Months Selected</h3>
              <p>When selecting ${selectedMonths.length} months, the payment amount must exactly equal the total fee.</p>
              <div class="validation-details">
                <div><strong>Amount Payable:</strong> ${formatCurrency(discountedTotal)}</div>
                <div><strong>Amount Paid:</strong> ${formatCurrency(feePaidValue)}</div>
                <div><strong>Difference:</strong> ${formatCurrency(discountedTotal - feePaidValue)}</div>
              </div>
              <p class="validation-note">For multiple months, you must pay the exact amount of ${formatCurrency(discountedTotal)}</p>
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: "swal-wide" },
        });
        return;
      }
    }

    const validationErrors = [];
    selectedMonths.forEach((month) => {
      const summary = getMonthPaymentSummary(month);
      const monthAmount = getMonthAmount(month);
      if (summary.isFullyPaid && monthAmount === 0) {
        validationErrors.push(
          `${month} is already fully paid (${formatCurrency(summary.totalPaid)}/${formatCurrency(summary.monthlyFee)})`
        );
      }
    });

    if (validationErrors.length > 0) {
      Swal.fire({
        title: "Validation Error",
        html: `
          <div class="validation-error">
            <h3>Some months are already paid</h3>
            <p>The following months cannot accept more payments:</p>
            <ul>
              ${validationErrors.map((error) => `<li>${error}</li>`).join("")}
            </ul>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    const paymentSummary = `
      <div class="payFamilyFee-paymentSummary">
        <h3>Payment Summary</h3>
        <div class="summary-details">
          <div><strong>Selected Months:</strong> ${selectedMonths.join(", ")}</div>
          <div><strong>Total Fee:</strong> ${formatCurrency(calculatedTotal)}</div>
          <div><strong>Discount:</strong> ${
            discountType === "percentage"
              ? discountValue + "%"
              : formatCurrency(discountValue)
          } (${formatCurrency(discountAmount)})</div>
          <div><strong>Amount Payable:</strong> ${formatCurrency(discountedTotal)}</div>
          <div><strong>Amount Paid:</strong> ${formatCurrency(feePaidValue)}</div>
          <div><strong>Balance:</strong> ${formatCurrency(discountedTotal - feePaidValue)}</div>
          <div><strong>Payment Mode:</strong> ${feeFormData.paymentMode}</div>
        </div>
        ${selectedMonths
          .map((month) => {
            const summary = getMonthPaymentSummary(month);
            const monthAmount = getMonthAmount(month);
            const monthStatus = getMonthStatus(month);
            const fixedFee = effectiveMonthlyFee;
            return `
            <div class="month-breakdown">
              <strong>${month}:</strong> 
              <span class="status-${monthStatus}">${monthStatus.toUpperCase()}</span> - 
              Paid: ${formatCurrency(summary.totalPaid)} / ${formatCurrency(fixedFee)}<br/>
              <small>Remaining: ${formatCurrency(monthAmount)} (${
              summary.paymentCount
            } payment${summary.paymentCount !== 1 ? "s" : ""})</small>
            </div>
          `;
          })
          .join("")}
      </div>
    `;

    const result = await Swal.fire({
      title: "Confirm Family Fee Payment",
      html: paymentSummary,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit Payment",
      cancelButtonText: "Review",
      customClass: { popup: "swal-wide" },
      width: "700px",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const loadingSwal = Swal.fire({
        title: "Processing Payment...",
        text: "Please wait while we process the fee payment.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const familyName = familyData?.familyName || `Family ${familyCode}`;
      const paymentDate = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const baseMonthlyFee = effectiveMonthlyFee;
      const timestamp = Date.now();
      const baseTransactionId =
        feeFormData.transactionId || `FAM-${familyCode}-${timestamp}`;
      const baseReceiptNo = `FAM-${familyCode}-${timestamp}`;

      let paymentResults = [];

      for (let i = 0; i < selectedMonths.length; i++) {
        const month = selectedMonths[i];
        const monthSummary = getMonthPaymentSummary(month);
        const monthAmount = getMonthAmount(month);
        const adjustedMonthlyFee = effectiveMonthlyFee;

        let monthPaid = 0;
        if (selectedMonths.length === 1) {
          monthPaid = feePaidValue;
        } else {
          const proportion = monthAmount / calculatedTotal;
          monthPaid =
            i === selectedMonths.length - 1
              ? feePaidValue - paymentResults.reduce((sum, r) => sum + r.amount, 0)
              : Math.round(feePaidValue * proportion * 100) / 100;
        }

        monthPaid = Math.min(monthPaid, monthAmount);
        if (monthPaid <= 0) continue;

        const cumulativePaid = monthSummary.totalPaid + monthPaid;
        const isFullyPaid = cumulativePaid >= adjustedMonthlyFee;
        const monthPending = isFullyPaid ? 0 : adjustedMonthlyFee - cumulativePaid;

        let fstatus = "unpaid";
        if (isFullyPaid) {
          fstatus = "paid";
        } else if (monthPaid > 0) {
          fstatus = "partial";
        }

        const paymentData = {
          family_code: familyCode,
          family_name: familyName,
          monthly_fee: adjustedMonthlyFee,
          adjusted_monthly_fee: adjustedMonthlyFee,
          original_monthly_fee: baseMonthlyFee,
          fee_paid: monthPaid,
          fee_pending: monthPending,
          paid_month: month,
          transaction_id: `${baseTransactionId}-${i + 1}`,
          receipt_no: `${baseReceiptNo}-${i + 1}`,
          payment_date: paymentDate,
          payment_mode: feeFormData.paymentMode,
          fstatus: fstatus,
          discount: discountAmount / selectedMonths.length,
          remarks: feeFormData.remarks || `Payment for ${month}`,
          has_inactive_students: familyData?.hasInactiveStudents || false,
          inactive_students_count: familyData?.inactiveStudentsCount || 0,
          cumulative_total_paid: cumulativePaid,
          previous_payments_count: monthSummary.paymentCount,
          is_full_payment: isFullyPaid,
          month_index: i + 1,
          total_months_in_bulk: selectedMonths.length,
        };

        console.log(`Processing payment for ${month}:`, paymentData);

        try {
          const response = await axios.post(
            "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=pay-family-monthly-fee",
            [paymentData],
            {
              timeout: 10000,
              headers: { "Content-Type": "application/json" },
            }
          );

          if (response.data.success) {
            paymentResults.push({
              month,
              amount: monthPaid,
              success: true,
              transactionId: paymentData.transaction_id,
              receiptNo: paymentData.receipt_no,
              status: fstatus,
            });
          } else {
            paymentResults.push({
              month,
              amount: monthPaid,
              success: false,
              error: response.data.error || "Unknown error",
            });
          }
        } catch (error) {
          paymentResults.push({
            month,
            amount: monthPaid,
            success: false,
            error: error.message,
          });
        }

        if (i < selectedMonths.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      Swal.close();

      const successfulPayments = paymentResults.filter((r) => r.success);
      const failedPayments = paymentResults.filter((r) => !r.success);

      if (successfulPayments.length > 0) {
        let successHTML = `
          <div class="payFamilyFee-successModal">
            <h3>✅ Payment Successfully Processed!</h3>
            <p>Successfully processed payments for ${successfulPayments.length} month(s)</p>
            <div class="payment-results">
              ${successfulPayments
                .map(
                  (payment) => `
                <div class="payment-result-item success">
                  <strong>${payment.month}:</strong> ${formatCurrency(payment.amount)} 
                  <span class="status-badge ${payment.status}">${payment.status.toUpperCase()}</span>
                  <br/>
                  <small>Receipt: ${payment.receiptNo}</small>
                </div>
              `
                )
                .join("")}
            </div>
        `;

        if (failedPayments.length > 0) {
          successHTML += `
            <div class="payment-failures">
              <h4>⚠️ Failed Payments:</h4>
              ${failedPayments
                .map(
                  (payment) => `
                <div class="payment-result-item failure">
                  <strong>${payment.month}:</strong> ${payment.error}
                </div>
              `
                )
                .join("")}
            </div>
          `;
        }

        successHTML += `
            <div class="payment-total">
              <strong>Total Paid:</strong> ${formatCurrency(
                successfulPayments.reduce((sum, p) => sum + p.amount, 0)
              )}
            </div>
          </div>
        `;

        Swal.fire({
          title: "Payment Complete",
          html: successHTML,
          icon: "success",
          confirmButtonText: "OK",
          width: "700px",
        }).then(() => {
          handleSearch();
          setSelectedMonths([]);
          setFeeFormData({
            feePaid: "",
            feePending: "0",
            totalFee: "0",
            discountType: "percentage",
            discountValue: "0",
            paymentMode: "cash",
            transactionId: "",
            remarks: "",
          });
        });
      } else {
        Swal.fire({
          title: "Payment Failed",
          html: `
            <div>
              <p>All payments failed. Please try again.</p>
              ${failedPayments
                .map((payment) => `<div>${payment.month}: ${payment.error}</div>`)
                .join("")}
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.close();
      console.error("Payment error:", error);
      Swal.fire({
        title: "Payment Error",
        text: error.message || "An error occurred during payment processing",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // ======================================================================
  // OTHER UI HANDLERS (UNCHANGED)
  // ======================================================================
  const showFamilyDetailsModal = () => {
    if (!familyData) {
      Swal.fire({
        title: "No Data",
        text: "No family data available!",
        icon: "error",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    Swal.fire({
      title: "Family Details",
      html: `
        <div class="payFamilyFee-familyModal">
          <div class="payFamilyFee-familyModalHeader">
            <FaUserFriends style="margin-right: 10px;" />
            Complete Family Information
            <span class="payFamilyFee-statusBadge ${familyFinancialStatus}">
              ${
                familyFinancialStatus === "old_student"
                  ? "ACTIVE"
                  : familyFinancialStatus === "new_student_with_setup"
                  ? "NEW"
                  : "SETUP REQUIRED"
              }
            </span>
          </div>
          <div class="payFamilyFee-familyModalGrid">
            <div class="payFamilyFee-familyModalField">
              <label>Family Code</label>
              <div class="payFamilyFee-familyModalValue">${familyData.familyCode}</div>
            </div>
            <div class="payFamilyFee-familyModalField">
              <label>Family Name</label>
              <div class="payFamilyFee-familyModalValue">${familyData.familyName}</div>
            </div>
            <div class="payFamilyFee-familyModalField">
              <label>Total Members</label>
              <div class="payFamilyFee-familyModalValue">${familyData.totalStudents}</div>
            </div>
            <div class="payFamilyFee-familyModalField">
              <label>Mobile</label>
              <div class="payFamilyFee-familyModalValue">${familyData.mobile || "N/A"}</div>
            </div>
            <div class="payFamilyFee-familyModalField">
              <label>Address</label>
              <div class="payFamilyFee-familyModalValue">${familyData.address || "N/A"}</div>
            </div>
          </div>
          <div class="payFamilyFee-familyMembers">
            <h4>Family Members</h4>
            <div class="payFamilyFee-membersList">
              ${familyMembers
                .map(
                  (member) => `
                <div class="payFamilyFee-memberItem">
                  <div class="payFamilyFee-memberAvatar">
                    ${member.student_name?.charAt(0)?.toUpperCase() || "S"}
                  </div>
                  <div class="payFamilyFee-memberInfo">
                    <div class="payFamilyFee-memberName">${member.student_name || "N/A"}</div>
                    <div class="payFamilyFee-memberDetails">
                      <span>${member.student_id || ""}</span>
                      <span class="payFamilyFee-feeAmount">${formatCurrency(member.monthly_fee || 0)}</span>
                    </div>
                  </div>
                </div>
              `
                )
                .join("")}
            </div>
          </div>
          <div class="payFamilyFee-familyModalFooter">
            <div class="payFamilyFee-familyModalStat">
              <div>Family Status</div>
              <div class="payFamilyFee-familyModalStatus ${familyFinancialStatus}">
                ${
                  familyFinancialStatus === "old_student"
                    ? "Active/Old Student"
                    : familyFinancialStatus === "new_student_with_setup"
                    ? "New Student (Setup Complete)"
                    : "Financial Setup Required"
                }
              </div>
            </div>
            <div class="payFamilyFee-familyModalStat">
              <div>Total Monthly Fee</div>
              <div class="payFamilyFee-familyModalAmount">${formatCurrency(calculateTotalMonthlyFee())}</div>
            </div>
          </div>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Close",
      width: "800px",
      customClass: { popup: "swal-wide" },
    });
  };

  const handleClearAll = () => {
    setSelectedMonths([]);
    setFeeFormData({
      feePaid: "",
      feePending: "0",
      totalFee: "0",
      discountType: "percentage",
      discountValue: "0",
      paymentMode: "cash",
      transactionId: "",
      remarks: "",
    });
  };

  const handlePrintReceipt = () => {
    if (selectedMonths.length === 0 || !feeFormData.feePaid) {
      Swal.fire({
        title: "No Payment",
        text: "Please complete a payment first.",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    const totalFee = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;
    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (totalFee * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
    discountAmount = Math.min(discountAmount, totalFee);
    const discountedTotal = totalFee - discountAmount;
    const feePaidValue = parseFloat(feeFormData.feePaid) || 0;

    const receiptWindow = window.open("", "_blank");
    receiptWindow.document.write(`
      <html>
        <head>
          <title>Family Fee Receipt - ${familyCode}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt { max-width: 500px; margin: 0 auto; border: 2px solid #000; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 5px 0; }
            .details { margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 5px 0; }
            .total { font-weight: bold; border-top: 1px solid #000; padding-top: 10px; margin-top: 10px; }
            .footer { text-align: center; margin-top: 30px; font-size: 12px; }
            .discount { color: #28a745; }
            .highlight { background-color: #f8f9fa; padding: 5px; border-radius: 3px; }
            .members { margin: 10px 0; font-size: 14px; }
            .status-badge { 
              display: inline-block; 
              padding: 3px 8px; 
              border-radius: 4px; 
              font-size: 12px; 
              font-weight: bold; 
              margin-left: 10px;
            }
            .status-old { background: #d4edda; color: #155724; }
            .status-new { background: #fff3cd; color: #856404; }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <h2>Family Fee Receipt</h2>
              <p>Receipt No: ${Date.now()}</p>
              <p>Date: ${moment().format("DD-MM-YYYY hh:mm A")}</p>
              <span class="status-badge ${
                familyFinancialStatus === "old_student" ? "status-old" : "status-new"
              }">
                ${
                  familyFinancialStatus === "old_student"
                    ? "ACTIVE FAMILY"
                    : "NEW FAMILY"
                }
              </span>
            </div>
            <div class="details">
              <div class="detail-row">
                <span>Family Code:</span>
                <span>${familyCode}</span>
              </div>
              <div class="detail-row">
                <span>Family Name:</span>
                <span>${familyData?.familyName || "Family"}</span>
              </div>
              <div class="detail-row">
                <span>Total Members:</span>
                <span>${familyMembers.length}</span>
              </div>
              <div class="detail-row">
                <span>Months:</span>
                <span>${selectedMonths.join(", ")}</span>
              </div>
              <div class="detail-row">
                <span>Payment Mode:</span>
                <span>${feeFormData.paymentMode}</span>
              </div>
              <div class="detail-row">
                <span>Transaction ID:</span>
                <span>${feeFormData.transactionId || "N/A"}</span>
              </div>
              <div class="members">
                <strong>Family Members:</strong>
                <ul>
                  ${familyMembers
                    .map(
                      (member) =>
                        `<li>${member.student_name} (${member.student_id})</li>`
                    )
                    .join("")}
                </ul>
              </div>
            </div>
            <div class="total">
              <div class="detail-row">
                <span>Total Fee:</span>
                <span>Rs. ${totalFee.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="detail-row discount">
                <span>Discount (${
                  discountType === "percentage"
                    ? discountValue + "%"
                    : formatCurrency(discountValue)
                }):</span>
                <span>- Rs. ${discountAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="detail-row highlight">
                <span>Amount Payable:</span>
                <span>Rs. ${discountedTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="detail-row">
                <span>Amount Paid:</span>
                <span>Rs. ${feePaidValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="detail-row">
                <span>Balance:</span>
                <span>Rs. ${(discountedTotal - feePaidValue).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}</span>
              </div>
            </div>
            <div class="footer">
              <p>Thank you for your payment!</p>
              <p>Generated on: ${moment().format("DD-MM-YYYY hh:mm A")}</p>
            </div>
          </div>
        </body>
      </html>
    `);
    receiptWindow.document.close();
    receiptWindow.print();
  };

  const handleNavigateToSoloFee = () => {
    navigate("/pay-solo-std-fee");
  };

  const getUnpaidMonthsCount = () => {
    if (familyFinancialStatus !== "old_student") return 0;
    return months.filter((m) => getMonthStatus(m) === "unpaid").length;
  };

  const getPaidMonthsCount = () => {
    if (familyFinancialStatus !== "old_student") return 0;
    return months.filter((m) => getMonthStatus(m) === "paid").length;
  };

  const getPartialMonthsCount = () => {
    if (familyFinancialStatus !== "old_student") return 0;
    return months.filter((m) => getMonthStatus(m) === "partial").length;
  };

  const currentMonthStatusDetails = getCurrentMonthStatusDetails();

  const handleTestConnection = async () => {
    setIsLoadingDb(true);
    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?test=true",
        { timeout: 5000 }
      );

      if (response.data.test_status === "OK" || response.data.success) {
        Swal.fire({
          title: "✅ Connection Successful",
          html: `
            <div style="text-align: left;">
              <p><strong>Database Status:</strong> Connected</p>
              <p><strong>Server Time:</strong> ${response.data.server_time || "N/A"}</p>
              <p><strong>PHP Version:</strong> ${response.data.php_version || "N/A"}</p>
              <p><strong>API Version:</strong> ${response.data.api_version || "N/A"}</p>
              <hr/>
              <h4>Table Status:</h4>
              <ul>
                ${
                  response.data.table_status
                    ? Object.entries(response.data.table_status)
                        .map(
                          ([table, status]) =>
                            `<li><strong>${table}:</strong> ${
                              status.exists
                                ? "✅ Exists (" + status.count + " records)"
                                : "❌ Not found"
                            }</li>`
                        )
                        .join("")
                    : "<li>No table status available</li>"
                }
              </ul>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
          customClass: { popup: "swal-wide" },
        });
        setIsDbConnected(true);
      } else {
        throw new Error("Test failed");
      }
    } catch (error) {
      console.error("❌ Connection test failed:", error);
      Swal.fire({
        title: "❌ Connection Failed",
        text: "Unable to connect to the database. Please check the server.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setIsDbConnected(false);
    } finally {
      setIsLoadingDb(false);
    }
  };

  const showInitialSetupDetails = () => {
    if (!initialSetupRecords || initialSetupRecords.length === 0) {
      Swal.fire({
        title: "No Setup Data",
        text: "No initial setup records found.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }

    const totals = calculateTotalsFromInitialSetup();

    Swal.fire({
      title: "Initial Fee Setup Details",
      html: `
        <div class="payFamilyFee-initialSetupModal">
          <div class="payFamilyFee-setupHeader">
            <FaUserPlus style="margin-right: 10px; color: #f39c12;" />
            <h3>Initial Admission Fee Setup</h3>
            <span class="payFamilyFee-setupStatus status-new">NEW STUDENT</span>
          </div>
          <div class="payFamilyFee-setupSummary">
            <div class="payFamilyFee-summaryItem">
              <span>Coverage:</span>
              <strong>${totals.coverage} Students</strong>
            </div>
            <div class="payFamilyFee-summaryItem">
              <span>Total Monthly Fee:</span>
              <strong>${formatCurrency(totals.totalMonthlyFee)}</strong>
            </div>
            <div class="payFamilyFee-summaryItem">
              <span>Total Pending:</span>
              <strong class="pending">${formatCurrency(totals.totalPending)}</strong>
            </div>
          </div>
          <div class="payFamilyFee-setupRecords">
            <h4>Student Fee Breakdown</h4>
            <div class="payFamilyFee-recordsList">
              ${initialSetupRecords
                .map(
                  (record) => `
                <div class="payFamilyFee-recordItem">
                  <div class="payFamilyFee-recordHeader">
                    <span class="payFamilyFee-studentName">${record.student_name}</span>
                    <span class="payFamilyFee-studentId">${record.student_id}</span>
                  </div>
                  <div class="payFamilyFee-recordDetails">
                    <div class="payFamilyFee-recordRow">
                      <span>Monthly Fee:</span>
                      <span>${formatCurrency(record.monthly_fee)}</span>
                    </div>
                    <div class="payFamilyFee-recordRow">
                      <span>Admission Fee:</span>
                      <span>${formatCurrency(record.admission_fee)}</span>
                    </div>
                    <div class="payFamilyFee-recordRow">
                      <span>Annual Charges:</span>
                      <span>${formatCurrency(record.annual_charges)}</span>
                    </div>
                    <div class="payFamilyFee-recordRow total">
                      <span>Total Pending:</span>
                      <strong>${formatCurrency(record.last_pendings)}</strong>
                    </div>
                  </div>
                </div>
              `
                )
                .join("")}
            </div>
          </div>
          <div class="payFamilyFee-setupNote">
            <FaInfoCircle style="color: #3498db; margin-right: 8px;" />
            <span>This is the initial fee setup. After first payment, recurring records will be created.</span>
          </div>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Proceed to Payment",
      showCancelButton: true,
      cancelButtonText: "Close",
      width: "800px",
      customClass: { popup: "swal-wide" },
    }).then((result) => {
      if (result.isConfirmed) {
        setActiveTab("fee");
      }
    });
  };

  // ======================================================================
  // RENDER METHODS – FULL, WITH SIMPLIFIED FEE DISPLAY
  // ======================================================================
  const FinancialIncompleteWarning = () => {
    if (!financialIncompleteWarning.show) return null;

    return (
      <div className="payFamilyFee-beautifulWarning">
        <div className="payFamilyFee-warningHeader">
          <FaExclamationTriangle className="payFamilyFee-warningIcon" />
          <h3>Financial Records Incomplete</h3>
          <button
            className="payFamilyFee-warningClose"
            onClick={() =>
              setFinancialIncompleteWarning({
                show: false,
                message: "",
                details: "",
              })
            }
          >
            <FaTimes />
          </button>
        </div>
        <div className="payFamilyFee-warningContent">
          <p className="payFamilyFee-warningMessage">{financialIncompleteWarning.message}</p>
          <p className="payFamilyFee-warningDetails">{financialIncompleteWarning.details}</p>
          <div className="payFamilyFee-warningActions">
            <button
              className="payFamilyFee-warningActionBtn primary"
              onClick={() => navigate("/fee-setup?family_code=" + familyCode)}
            >
              <FaTools className="payFamilyFee-warningActionIcon" />
              Complete Financial Setup
            </button>
            <button
              className="payFamilyFee-warningActionBtn secondary"
              onClick={handleSearch}
            >
              <FaSync className="payFamilyFee-warningActionIcon" />
              Re-check Status
            </button>
            <button
              className="payFamilyFee-warningActionBtn info"
              onClick={() => {
                Swal.fire({
                  title: "What does this mean?",
                  html: `
                    <div class="payFamilyFee-helpModal">
                      <h4>Financial Records Incomplete</h4>
                      <p>This family exists in the system but the financial setup is not complete. Here's what you need to do:</p>
                      <ol>
                        <li><strong>Initial Fee Setup Required:</strong> Monthly fees, admission fees, or other charges need to be configured.</li>
                        <li><strong>Missing Ledger Records:</strong> Active payment records for this family are not yet created.</li>
                        <li><strong>Action:</strong> Click "Complete Financial Setup" to configure the initial fees.</li>
                      </ol>
                      <p>After setup, you can return here to make payments.</p>
                    </div>
                  `,
                  icon: "info",
                  confirmButtonText: "Got it!",
                  width: "600px",
                });
              }}
            >
              <FaInfoCircle className="payFamilyFee-warningActionIcon" />
              Learn More
            </button>
          </div>
        </div>
        <div className="payFamilyFee-warningFooter">
          <FaInfoCircle className="payFamilyFee-footerIcon" />
          <span>This family cannot receive payments until financial setup is complete.</span>
        </div>
      </div>
    );
  };

  const renderContentBasedOnStatus = () => {
    switch (familyFinancialStatus) {
      case "old_student":
        return renderOldStudentContent();
      case "new_student_with_setup":
        return renderNewStudentContent();
      case "financial_limbo":
        return renderFinancialLimboContent();
      default:
        return renderNoFamilyContent();
    }
  };

  const renderOldStudentContent = () => (
    <>
      <div className="payFamilyFee-tabs">
        <button
          className={`payFamilyFee-tab ${activeTab === "fee" ? "payFamilyFee-tabActive" : ""}`}
          onClick={() => setActiveTab("fee")}
        >
          <FaCreditCard className="payFamilyFee-tabIcon" />
          Fee Payment
        </button>
        <button
          className={`payFamilyFee-tab ${activeTab === "expense" ? "payFamilyFee-tabActive" : ""}`}
          onClick={() => setActiveTab("expense")}
        >
          <FaMoneyBillWave className="payFamilyFee-tabIcon" />
          Expense Record
        </button>
        <button
          className={`payFamilyFee-tab ${activeTab === "familyPaymentHistory" ? "payFamilyFee-tabActive" : ""}`}
          onClick={() => setActiveTab("familyPaymentHistory")}
        >
          <FaFileInvoiceDollar className="payFamilyFee-tabIcon" />
          Family Payment History
        </button>
      </div>

      {activeTab === "fee" && (
        <div className="payFamilyFee-topCards">
          <div className="payFamilyFee-topCard payFamilyFee-pendingFeeCard">
            <div className="payFamilyFee-topCardHeader">
              <FaMoneyBillWave className="payFamilyFee-topCardIcon" />
              <h3>Total Pending Fee</h3>
            </div>
            <div className="payFamilyFee-topCardContent">
              <div className="payFamilyFee-topCardValue">
                {formatCurrency(calculateTotalPendingFee())}
              </div>
              <div className="payFamilyFee-topCardSubtitle">
                For {familyMembers.length} family members
              </div>
              <div className="payFamilyFee-topCardDetails">
                <div className="payFamilyFee-topCardDetail">
                  <span>Unpaid Months:</span>
                  <strong>{getUnpaidMonthsCount()}</strong>
                </div>
                <div className="payFamilyFee-topCardDetail">
                  <span>Partial Months:</span>
                  <strong>{getPartialMonthsCount()}</strong>
                </div>
                <div className="payFamilyFee-topCardDetail">
                  <span>Paid Months:</span>
                  <strong>{getPaidMonthsCount()}</strong>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`payFamilyFee-topCard payFamilyFee-currentMonthCard status-${currentMonthStatusDetails.status}`}
          >
            <div className="payFamilyFee-topCardHeader">
              <FaCalendarDay className="payFamilyFee-topCardIcon" />
              <h3>Current Month Status</h3>
              <span className="payFamilyFee-currentMonthName">
                {moment().format("MMMM")}
              </span>
            </div>
            <div className="payFamilyFee-topCardContent">
              <div className="payFamilyFee-currentMonthStatus">
                {getCurrentMonthStatusIcon()}
                <span className={`payFamilyFee-statusText ${currentMonthStatusDetails.status}`}>
                  {currentMonthStatusDetails.status.toUpperCase()}
                </span>
              </div>
              <div className="payFamilyFee-topCardDetails">
                <div className="payFamilyFee-topCardDetail">
                  <span>Monthly Fee:</span>
                  <strong>{formatCurrency(currentMonthStatusDetails.amount)}</strong>
                </div>
                <div className="payFamilyFee-topCardDetail">
                  <span>Amount Paid:</span>
                  <strong>{formatCurrency(currentMonthStatusDetails.paid)}</strong>
                </div>
                <div className="payFamilyFee-topCardDetail">
                  <span>Amount Pending:</span>
                  <strong className="pending">{formatCurrency(currentMonthStatusDetails.pending)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="payFamilyFee-topCard payFamilyFee-membersCard">
            <div className="payFamilyFee-topCardHeader">
              <FaUserFriends className="payFamilyFee-topCardIcon" />
              <h3>Family Members</h3>
            </div>
            <div className="payFamilyFee-topCardContent">
              <div className="payFamilyFee-topCardValue">{familyMembers.length}</div>
              <div className="payFamilyFee-topCardSubtitle">
                Total Monthly Fee: {formatCurrency(calculateTotalMonthlyFee())}
              </div>
              <div className="payFamilyFee-membersList">
                {familyMembers.slice(0, 3).map((member, index) => (
                  <div key={index} className="payFamilyFee-memberPreview">
                    <div className="payFamilyFee-memberAvatar">
                      {member.student_name?.charAt(0)?.toUpperCase() || "S"}
                    </div>
                    <div className="payFamilyFee-memberInfo">
                      <div className="payFamilyFee-memberName">{member.student_name}</div>
                      <div className="payFamilyFee-memberClass">{member.student_id}</div>
                    </div>
                  </div>
                ))}
                {familyMembers.length > 3 && (
                  <div className="payFamilyFee-moreMembers">+{familyMembers.length - 3} more members</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="payFamilyFee-familyCard">
        <div className="payFamilyFee-familyCardHeader">
          <FaHome className="payFamilyFee-familyIcon" />
          <h3>Family Information</h3>
          <div className="payFamilyFee-familyBadge">{familyMembers.length} Members</div>
          <span className="payFamilyFee-statusIndicator old">ACTIVE FAMILY</span>
        </div>

        <div className="payFamilyFee-familyInfo">
          <div className="payFamilyFee-familyRow">
            <div className="payFamilyFee-familyField">
              <div className="payFamilyFee-familyFieldIcon">
                <FaHome />
              </div>
              <div>
                <div className="payFamilyFee-fieldLabel">Family Code</div>
                <div className="payFamilyFee-fieldValue">{familyData.familyCode}</div>
              </div>
            </div>
            <div className="payFamilyFee-familyField">
              <div className="payFamilyFee-familyFieldIcon">
                <FaUserFriends />
              </div>
              <div>
                <div className="payFamilyFee-fieldLabel">Family Name</div>
                <div className="payFamilyFee-fieldValue">{familyData.familyName}</div>
              </div>
            </div>
            <div className="payFamilyFee-familyField">
              <div className="payFamilyFee-familyFieldIcon">
                <FaPhone />
              </div>
              <div>
                <div className="payFamilyFee-fieldLabel">Mobile</div>
                <div className="payFamilyFee-fieldValue">{familyData.mobile || "N/A"}</div>
              </div>
            </div>
          </div>

          {/* Simple inactive student info – no fee math involved */}
          {familyData?.hasInactiveStudents && (
            <div className="payFamilyFee-inactiveWarning">
              <FaExclamationTriangle className="payFamilyFee-inactiveIcon" />
              <span>{familyData.inactiveStudentsCount} inactive student(s) – fixed fee applied</span>
            </div>
          )}

          <div className="payFamilyFee-familyStatusStats">
            <div className="payFamilyFee-statusStat active">
              <FaUser className="payFamilyFee-statusIcon" />
              <span>Active: {familyData?.activeStudentsCount || 0}</span>
            </div>
            <div className="payFamilyFee-statusStat inactive">
              <FaUser className="payFamilyFee-statusIcon" />
              <span>Inactive: {familyData?.inactiveStudentsCount || 0}</span>
            </div>
          </div>

          <div className="payFamilyFee-familyActions">
            <button className="payFamilyFee-viewDetailsBtn" onClick={showFamilyDetailsModal}>
              <FaEye className="payFamilyFee-viewIcon" />
              View Complete Details
            </button>
          </div>
        </div>
      </div>

      <div className="payFamilyFee-content">
        {activeTab === "fee" && (
          <div className="payFamilyFee-feeContainer">
            <div className="payFamilyFee-feeFormFull">
              <div className="payFamilyFee-monthSection">
                <div className="payFamilyFee-monthHeader">
                  <FaCalendarAlt className="payFamilyFee-monthHeaderIcon" />
                  <h3>Select Payment Months</h3>
                  <div className="payFamilyFee-monthYear">Year: {currentYear}</div>
                </div>

                <div className="payFamilyFee-monthGrid">
                  {months.map((month, index) => {
                    const status = getMonthStatus(month);
                    const isSelected = selectedMonths.includes(month);
                    const amount = getMonthAmount(month);

                    return (
                      <div
                        key={index}
                        className={`payFamilyFee-monthCard ${status} ${
                          isSelected ? "payFamilyFee-monthCardSelected" : ""
                        }`}
                        onClick={() => handleCheckboxChange(month)}
                        title={`${month} - ${formatCurrency(amount)}`}
                      >
                        <div className="payFamilyFee-monthCardHeader">
                          {getMonthIcon(month)}
                          <span className="payFamilyFee-monthName">{month.substring(0, 3)}</span>
                        </div>
                        <div className="payFamilyFee-monthAmount">{renderMonthFeeDisplay(month)}</div>
                        <div className="payFamilyFee-monthStatus">
                          <span className={`payFamilyFee-statusIndicator ${status}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="payFamilyFee-monthCheck">
                            <FaCheckCircle />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedMonths.length > 0 && (
                  <div className="payFamilyFee-monthSummary">
                    <div className="payFamilyFee-summaryItem">
                      <span>Selected Months</span>
                      <div className="payFamilyFee-summaryValue">
                        <strong>{selectedMonths.length}</strong>
                        <span>Months</span>
                      </div>
                    </div>
                    <div className="payFamilyFee-summaryItem">
                      <span>Total Amount</span>
                      <div className="payFamilyFee-summaryValue">
                        <strong className="payFamilyFee-totalAmount">{formatCurrency(calculateTotalFee())}</strong>
                      </div>
                    </div>
                    <div className="payFamilyFee-summaryItem">
                      <span>Status</span>
                      <div className="payFamilyFee-summaryValue">
                        <strong
                          className={`payFamilyFee-statusBadge ${
                            selectedMonths.length === 1 ? "single" : "multiple"
                          }`}
                        >
                          {selectedMonths.length === 1 ? "Single Month" : "Multiple Months"}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="payFamilyFee-paymentForm">
                <div className="payFamilyFee-formHeader">
                  <FaCreditCard className="payFamilyFee-formHeaderIcon" />
                  <h3>Family Fee Payment Details</h3>
                </div>

                <form onSubmit={handleFeeSubmit}>
                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Total Monthly Fee
                      </label>
                      <input
                        type="text"
                        className="payFamilyFee-formInput"
                        value={formatCurrency(calculateTotalMonthlyFee())}
                        readOnly
                      />
                    </div>
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaCalendarAlt className="payFamilyFee-labelIcon" />
                        Selected Months
                      </label>
                      <div className="payFamilyFee-selectedMonths">
                        {selectedMonths.length > 0 ? (
                          selectedMonths.map((month, index) => (
                            <span key={index} className="payFamilyFee-monthTag" onClick={() => handleCheckboxChange(month)}>
                              {month}
                              <FaTimes className="payFamilyFee-monthTagClose" />
                            </span>
                          ))
                        ) : (
                          <span className="payFamilyFee-noMonths">
                            No months selected. Please select months from above.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Total Fee Amount
                      </label>
                      <input
                        type="text"
                        className="payFamilyFee-formInput"
                        value={formatCurrency(calculateTotalFee())}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaPercent className="payFamilyFee-labelIcon" />
                        Discount Type
                      </label>
                      <select
                        className="payFamilyFee-formSelect"
                        value={feeFormData.discountType}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const totalFee = calculateTotalFee();
                          let newDiscountValue = parseFloat(feeFormData.discountValue) || 0;

                          if (newType === "percentage" && feeFormData.discountType === "fixed") {
                            newDiscountValue = totalFee > 0 ? (newDiscountValue / totalFee) * 100 : 0;
                          } else if (newType === "fixed" && feeFormData.discountType === "percentage") {
                            newDiscountValue = (totalFee * newDiscountValue) / 100;
                          }

                          let discountAmount = 0;
                          if (newType === "percentage") {
                            discountAmount = (totalFee * newDiscountValue) / 100;
                          } else {
                            discountAmount = newDiscountValue;
                          }

                          const discountedTotal = totalFee - discountAmount;
                          const feePaidValue = parseFloat(feeFormData.feePaid) || 0;
                          const pendingAmount = discountedTotal - feePaidValue;

                          setFeeFormData((prev) => ({
                            ...prev,
                            discountType: newType,
                            discountValue: newDiscountValue.toString(),
                            feePending: (pendingAmount >= 0 ? pendingAmount : 0).toString(),
                          }));
                        }}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (Rs.)</option>
                      </select>
                    </div>

                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Discount {feeFormData.discountType === "percentage" ? "(%)" : "(Rs.)"}
                      </label>
                      <input
                        type="number"
                        className="payFamilyFee-formInput"
                        value={feeFormData.discountValue}
                        onChange={(e) => {
                          const discountValue = parseFloat(e.target.value) || 0;
                          const totalFee = calculateTotalFee();
                          const discountType = feeFormData.discountType;

                          let discountAmount = 0;
                          if (discountType === "percentage") {
                            discountAmount = (totalFee * discountValue) / 100;
                          } else {
                            discountAmount = discountValue;
                          }

                          const discountedTotal = totalFee - discountAmount;
                          const feePaidValue = parseFloat(feeFormData.feePaid) || 0;
                          const pendingAmount = discountedTotal - feePaidValue;

                          setFeeFormData((prev) => ({
                            ...prev,
                            discountValue: e.target.value,
                            feePending: (pendingAmount >= 0 ? pendingAmount : 0).toString(),
                          }));
                        }}
                        placeholder={feeFormData.discountType === "percentage" ? "0" : "0"}
                        min="0"
                        step="0.01"
                        max={feeFormData.discountType === "percentage" ? "100" : undefined}
                      />
                    </div>
                  </div>

                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Discount Amount
                      </label>
                      <input
                        type="text"
                        className="payFamilyFee-formInput payFamilyFee-discountedAmount"
                        value={formatCurrency(calculateFeeDiscountAmount())}
                        readOnly
                      />
                    </div>
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Amount After Discount
                      </label>
                      <input
                        type="text"
                        className="payFamilyFee-formInput payFamilyFee-discountedAmount"
                        value={formatCurrency(calculateDiscountedFee())}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Fee Paid
                      </label>
                      <input
                        type="number"
                        className="payFamilyFee-formInput"
                        value={feeFormData.feePaid}
                        onChange={handleFeePaidChange}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Fee Pending
                      </label>
                      <input
                        type="text"
                        className="payFamilyFee-formInput"
                        value={formatCurrency(feeFormData.feePending)}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="payFamilyFee-formRow">
                    <div className="payFamilyFee-formGroup">
                      <label className="payFamilyFee-formLabel">
                        <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                        Payment Mode
                      </label>
                      <select
                        className="payFamilyFee-formSelect"
                        value={feeFormData.paymentMode}
                        onChange={(e) =>
                          setFeeFormData((prev) => ({ ...prev, paymentMode: e.target.value }))
                        }
                      >
                        <option value="cash">Cash</option>
                        <option value="bank">Bank Transfer</option>
                        <option value="card">Card</option>
                        <option value="online">Online Payment</option>
                      </select>
                    </div>
                  </div>

                  <div className="payFamilyFee-formActions">
                    <button
                      type="button"
                      className="payFamilyFee-clearBtn"
                      onClick={handleClearAll}
                      disabled={selectedMonths.length === 0 && !feeFormData.feePaid}
                    >
                      <FaTimes className="payFamilyFee-clearIcon" />
                      Clear All
                    </button>
                    <button
                      type="button"
                      className="payFamilyFee-printBtn"
                      onClick={handlePrintReceipt}
                      disabled={selectedMonths.length === 0 || !feeFormData.feePaid}
                    >
                      <FaFileInvoiceDollar className="payFamilyFee-printIcon" />
                      Print Receipt
                    </button>
                    <button
                      type="submit"
                      className="payFamilyFee-submitBtn"
                      disabled={selectedMonths.length === 0 || !feeFormData.feePaid}
                    >
                      <FaPaperPlane className="payFamilyFee-submitIcon" />
                      Submit Family Fee Payment
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {activeTab === "expense" && (
          <div className="payFamilyFee-expenseTab" style={{ width: "90%", marginLeft: "-2rem" }}>
            <div className="payFamilyFee-sectionHeader">
              <h2 className="payFamilyFee-sectionTitle">
                <FaMoneyBillWave className="payFamilyFee-sectionIcon" />
                Family Expense Management
              </h2>
              <p className="payFamilyFee-sectionDescription">
                Manage all expense records for {familyData.familyName} family
              </p>
            </div>
            <FamilyStudentExpense
              familyData={familyData}
              familyMembers={familyMembers}
              onRefresh={handleSearch}
              fullScreenMode={true}
            />
          </div>
        )}

        {activeTab === "familyPaymentHistory" && (
          <div className="payFamilyFee-familyPaymentHistoryTab" style={{ width: "90%", marginLeft: "-2rem" }}>
            <FamilyPaymentHistory familyData={familyData} />
          </div>
        )}
      </div>
    </>
  );

  const renderNewStudentContent = () => {
    const totals = calculateTotalsFromInitialSetup();

    return (
      <>
        <div className="payFamilyFee-tabs">
          <button className="payFamilyFee-tab payFamilyFee-tabActive" onClick={() => setActiveTab("fee")}>
            <FaUserPlus className="payFamilyFee-tabIcon" />
            Initial Fee Payment
          </button>
          <button className="payFamilyFee-tab" onClick={showInitialSetupDetails}>
            <FaListAlt className="payFamilyFee-tabIcon" />
            View Setup Details
          </button>
        </div>

        <div className="payFamilyFee-statusBanner status-new">
          <div className="payFamilyFee-statusBannerContent">
            <FaUserPlus className="payFamilyFee-statusIcon" />
            <div>
              <h3>New Student - Initial Setup Complete</h3>
              <p>Family has initial admission fee setup. First payment will activate recurring records.</p>
            </div>
          </div>
        </div>

        <div className="payFamilyFee-familyCard">
          <div className="payFamilyFee-familyCardHeader">
            <FaHome className="payFamilyFee-familyIcon" />
            <h3>Family Information</h3>
            <div className="payFamilyFee-familyBadge">{familyMembers.length} Members</div>
            <span className="payFamilyFee-statusIndicator new">NEW FAMILY</span>
          </div>

          <div className="payFamilyFee-familyInfo">
            <div className="payFamilyFee-familyRow">
              <div className="payFamilyFee-familyField">
                <div className="payFamilyFee-familyFieldIcon">
                  <FaHome />
                </div>
                <div>
                  <div className="payFamilyFee-fieldLabel">Family Code</div>
                  <div className="payFamilyFee-fieldValue">{familyData.familyCode}</div>
                </div>
              </div>
              <div className="payFamilyFee-familyField">
                <div className="payFamilyFee-familyFieldIcon">
                  <FaUserFriends />
                </div>
                <div>
                  <div className="payFamilyFee-fieldLabel">Family Name</div>
                  <div className="payFamilyFee-fieldValue">{familyData.familyName}</div>
                </div>
              </div>
              <div className="payFamilyFee-familyField">
                <div className="payFamilyFee-familyFieldIcon">
                  <FaPhone />
                </div>
                <div>
                  <div className="payFamilyFee-fieldLabel">Mobile</div>
                  <div className="payFamilyFee-fieldValue">{familyData.mobile || "N/A"}</div>
                </div>
              </div>
            </div>

            <div className="payFamilyFee-familyActions">
              <button className="payFamilyFee-viewDetailsBtn" onClick={showFamilyDetailsModal}>
                <FaEye className="payFamilyFee-viewIcon" />
                View Complete Details
              </button>
              <button className="payFamilyFee-viewSetupBtn" onClick={showInitialSetupDetails}>
                <FaListAlt className="payFamilyFee-viewIcon" />
                View Initial Setup
              </button>
            </div>
          </div>
        </div>

        <div className="payFamilyFee-initialSetupSummary">
          <div className="payFamilyFee-sectionHeader">
            <h2 className="payFamilyFee-sectionTitle">
              <FaReceipt className="payFamilyFee-sectionIcon" />
              Initial Setup Summary
            </h2>
            <p className="payFamilyFee-sectionDescription">
              Review the initial admission fee setup before making first payment
            </p>
          </div>

          <div className="payFamilyFee-summaryCards">
            <div className="payFamilyFee-summaryCard">
              <div className="payFamilyFee-summaryCardHeader">
                <FaUserFriends className="payFamilyFee-summaryIcon" />
                <h4>Student Coverage</h4>
              </div>
              <div className="payFamilyFee-summaryCardContent">
                <div className="payFamilyFee-summaryCardValue">{totals.coverage}</div>
                <div className="payFamilyFee-summaryCardLabel">Students with Fee Setup</div>
              </div>
            </div>
            <div className="payFamilyFee-summaryCard">
              <div className="payFamilyFee-summaryCardHeader">
                <FaMoneyBillWave className="payFamilyFee-summaryIcon" />
                <h4>Total Monthly Fee</h4>
              </div>
              <div className="payFamilyFee-summaryCardContent">
                <div className="payFamilyFee-summaryCardValue">{formatCurrency(totals.totalMonthlyFee)}</div>
                <div className="payFamilyFee-summaryCardLabel">Monthly Fee Total</div>
              </div>
            </div>
            <div className="payFamilyFee-summaryCard">
              <div className="payFamilyFee-summaryCardHeader">
                <FaExclamationCircle className="payFamilyFee-summaryIcon" />
                <h4>Total Pending</h4>
              </div>
              <div className="payFamilyFee-summaryCardContent">
                <div className="payFamilyFee-summaryCardValue pending">{formatCurrency(totals.totalPending)}</div>
                <div className="payFamilyFee-summaryCardLabel">Initial Balance</div>
              </div>
            </div>
          </div>

          <div className="payFamilyFee-initialPaymentForm">
            <div className="payFamilyFee-sectionHeader">
              <h2 className="payFamilyFee-sectionTitle">
                <FaCreditCard className="payFamilyFee-sectionIcon" />
                First Fee Payment
              </h2>
              <p className="payFamilyFee-sectionDescription">
                Make the first payment to activate recurring fee records
              </p>
            </div>

            <div className="payFamilyFee-paymentForm">
              <form onSubmit={handleFeeSubmit}>
                <div className="payFamilyFee-formRow">
                  <div className="payFamilyFee-formGroup">
                    <label className="payFamilyFee-formLabel">
                      <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                      Total Amount Due
                    </label>
                    <input
                      type="text"
                      className="payFamilyFee-formInput"
                      value={formatCurrency(totals.totalPending)}
                      readOnly
                    />
                  </div>
                  <div className="payFamilyFee-formGroup">
                    <label className="payFamilyFee-formLabel">
                      <FaPercent className="payFamilyFee-labelIcon" />
                      Discount Type
                    </label>
                    <select
                      className="payFamilyFee-formSelect"
                      value={feeFormData.discountType}
                      onChange={(e) => setFeeFormData((prev) => ({ ...prev, discountType: e.target.value }))}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (Rs.)</option>
                    </select>
                  </div>
                </div>

                <div className="payFamilyFee-formRow">
                  <div className="payFamilyFee-formGroup">
                    <label className="payFamilyFee-formLabel">
                      <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                      Discount {feeFormData.discountType === "percentage" ? "(%)" : "(Rs.)"}
                    </label>
                    <input
                      type="number"
                      className="payFamilyFee-formInput"
                      value={feeFormData.discountValue}
                      onChange={(e) => setFeeFormData((prev) => ({ ...prev, discountValue: e.target.value }))}
                      min="0"
                      step="0.01"
                      max={feeFormData.discountType === "percentage" ? "100" : undefined}
                    />
                  </div>
                  <div className="payFamilyFee-formGroup">
                    <label className="payFamilyFee-formLabel">
                      <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                      Amount After Discount
                    </label>
                    <input
                      type="text"
                      className="payFamilyFee-formInput payFamilyFee-discountedAmount"
                      value={formatCurrency(
                        totals.totalPending -
                          (feeFormData.discountType === "percentage"
                            ? (totals.totalPending * parseFloat(feeFormData.discountValue || 0)) / 100
                            : parseFloat(feeFormData.discountValue || 0))
                      )}
                      readOnly
                    />
                  </div>
                </div>

                <div className="payFamilyFee-formGroup">
                  <label className="payFamilyFee-formLabel">
                    <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                    Amount Paid
                  </label>
                  <input
                    type="number"
                    className="payFamilyFee-formInput"
                    value={feeFormData.feePaid}
                    onChange={(e) =>
                      setFeeFormData((prev) => ({
                        ...prev,
                        feePaid: e.target.value,
                        feePending: (totals.totalPending - parseFloat(e.target.value || 0)).toString(),
                      }))
                    }
                    placeholder="Enter payment amount"
                    min="0"
                    step="0.01"
                    max={totals.totalPending}
                  />
                </div>

                <div className="payFamilyFee-formGroup">
                  <label className="payFamilyFee-formLabel">
                    <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                    Remaining Balance
                  </label>
                  <input
                    type="text"
                    className="payFamilyFee-formInput"
                    value={formatCurrency(feeFormData.feePending)}
                    readOnly
                  />
                </div>

                <div className="payFamilyFee-formRow">
                  <div className="payFamilyFee-formGroup">
                    <label className="payFamilyFee-formLabel">
                      <FaMoneyBillWave className="payFamilyFee-labelIcon" />
                      Payment Mode
                    </label>
                    <select
                      className="payFamilyFee-formSelect"
                      value={feeFormData.paymentMode}
                      onChange={(e) => setFeeFormData((prev) => ({ ...prev, paymentMode: e.target.value }))}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="card">Card</option>
                      <option value="online">Online Payment</option>
                    </select>
                  </div>
                </div>

                <div className="payFamilyFee-formActions">
                  <button type="button" className="payFamilyFee-clearBtn" onClick={handleClearAll}>
                    <FaTimes className="payFamilyFee-clearIcon" />
                    Clear
                  </button>
                  <button
                    type="submit"
                    className="payFamilyFee-submitBtn"
                    disabled={!feeFormData.feePaid || parseFloat(feeFormData.feePaid) <= 0}
                  >
                    <FaPaperPlane className="payFamilyFee-submitIcon" />
                    Submit First Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderFinancialLimboContent = () => (
    <div className="payFamilyFee-limboContent">
      <div className="payFamilyFee-limboHeader">
        <FaExclamationTriangle className="payFamilyFee-limboIcon" />
        <h2>Financial Setup Required</h2>
        <p>This family exists but has no financial setup</p>
      </div>

      <div className="payFamilyFee-limboCard">
        <div className="payFamilyFee-limboInfo">
          <h3>Family Details</h3>
          <div className="payFamilyFee-limboDetails">
            <div className="payFamilyFee-limboDetail">
              <strong>Family Code:</strong> {familyData?.familyCode}
            </div>
            <div className="payFamilyFee-limboDetail">
              <strong>Family Name:</strong> {familyData?.familyName}
            </div>
            <div className="payFamilyFee-limboDetail">
              <strong>Total Students:</strong> {familyMembers.length}
            </div>
            <div className="payFamilyFee-limboDetail">
              <strong>Mobile:</strong> {familyData?.mobile || "N/A"}
            </div>
          </div>
        </div>

        <div className="payFamilyFee-limboAction">
          <h3>Action Required</h3>
          <p>Complete the initial admission fee setup before processing payments.</p>
          <div className="payFamilyFee-limboSteps">
            <h4>Next Steps:</h4>
            <ol>
              <li>Go to Fee Setup section</li>
              <li>Select family: {familyData?.familyCode}</li>
              <li>Enter all initial charges (Admission, Annual, Books, etc.)</li>
              <li>Save the initial fee setup</li>
              <li>Return here to make first payment</li>
            </ol>
          </div>
          <div className="payFamilyFee-limboButtons">
            <button
              className="payFamilyFee-setupBtn"
              onClick={() => navigate("/fee-setup?family_code=" + familyCode)}
            >
              <FaTools className="payFamilyFee-setupIcon" />
              Go to Fee Setup
            </button>
            <button className="payFamilyFee-refreshBtn" onClick={handleSearch}>
              <FaSync className="payFamilyFee-refreshIcon" />
              Refresh Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNoFamilyContent = () => (
    <div className="payFamilyFee-emptyState">
      <div className="payFamilyFee-emptyStateIcon">
        <FaSearch />
      </div>
      <h3>Search for a Family</h3>
      <p>Enter a Family Code above to view and manage family fee payments and expenses.</p>
      <div className="payFamilyFee-emptyStateTips">
        <h4>Family Status Flow:</h4>
        <ul>
          <li>
            <strong>Active/Old Student:</strong> Has recurring fee records - Full access to all features
          </li>
          <li>
            <strong>New Student with Setup:</strong> Initial setup complete - Make first payment to activate
          </li>
          <li>
            <strong>Financial Limbo:</strong> No financial setup - Complete initial setup first
          </li>
        </ul>
        <p className="payFamilyFee-note">
          <FaInfoCircle style={{ marginRight: "8px" }} />
          The system automatically determines the family's financial status
        </p>
      </div>
      <button className="payFamilyFee-testConnectionBtn" onClick={handleTestConnection}>
        <FaDatabase className="payFamilyFee-dbIcon" />
        Test Database Connection
      </button>
    </div>
  );

  const getFooterText = () => {
    if (!familyData) return "Family Fee Management System";

    switch (familyFinancialStatus) {
      case "old_student":
        return `Managing ACTIVE family: ${familyData.familyName} | Code: ${familyData.familyCode} | Members: ${familyMembers.length}`;
      case "new_student_with_setup":
        return `NEW FAMILY - Initial Setup Complete: ${familyData.familyName} | Code: ${familyData.familyCode} | Members: ${familyMembers.length}`;
      case "financial_limbo":
        return `SETUP REQUIRED: ${familyData.familyName} | Code: ${familyData.familyCode} | Members: ${familyMembers.length}`;
      default:
        return "Family Fee Management System";
    }
  };

  // ======================================================================
  // MAIN RENDER
  // ======================================================================
  return (
    <div className="payFamilyFee-main">
      <Sidebar />

      <div className="main-content">
        <header className="payFamilyFee-header">
          <div className="payFamilyFee-headerContent">
            <h1 className="payFamilyFee-title">
              <FaUsers className="payFamilyFee-titleIcon" />
              Family Fee & Expense Management
            </h1>
            <p className="payFamilyFee-subtitle">
              Pay monthly fees and record expenses for family groups
            </p>
            <div className="payFamilyFee-connectionStatus">
              <div
                className={`payFamilyFee-statusDot ${
                  isDbConnected ? "connected" : "disconnected"
                }`}
              ></div>
              <span>Database: {isDbConnected ? "Connected" : "Disconnected"}</span>
              <button
                className="payFamilyFee-testBtn"
                onClick={handleTestConnection}
                disabled={isLoadingDb}
              >
                {isLoadingDb ? "Testing..." : "Test Connection"}
              </button>
            </div>
          </div>
        </header>

        <section className="payFamilyFee-searchSection" style={{ marginTop: "1rem" }}>
          <div className="payFamilyFee-searchContainer">
            <div className="payFamilyFee-searchGroup" style={{ height: "30vh", boxSizing: "border-box" }}>
              <label className="payFamilyFee-searchLabel">
                <FaHome className="payFamilyFee-searchIcon" />
                Enter Family Code
              </label>
              <div className="family-input-group" style={{ width: "70%" }}>
                <input
                  type="text"
                  value={familyCode}
                  onChange={handleFamilyCodeChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter family code (e.g., FAM-001)"
                  className="family-search-input"
                  disabled={isLoading}
                  ref={inputRef}
                />
                {familyCodeSuggestions.length > 0 && (
                  <div
                    className="family-suggestion-list"
                    style={{
                      width: "85%",
                      maxHeight: "50vh",
                      overflowY: "auto",
                      overflowX: "scroll",
                      position: "absolute",
                      zIndex: 1000,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      marginTop: "2px",
                    }}
                  >
                    {familyCodeSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="family-suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion, "familyCode")}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                      >
                        <FiCreditCard
                          className="family-suggestion-icon"
                          style={{ marginRight: "10px", color: "#666" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div
                            className="family-suggestion-main"
                            style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}
                          >
                            {suggestion.family_code}
                          </div>
                          <div
                            className="family-suggestion-sub"
                            style={{ color: "#666", fontSize: "12px", marginTop: "2px" }}
                          >
                            {suggestion.family_name}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="payFamilyFee-idHint">Format: F-DEAXXX (e.g., F-DEA001)</div>
            </div>

            <div className="payFamilyFee-searchActions">
              <button
                className="payFamilyFee-searchBtn"
                onClick={handleSearch}
                disabled={isLoading || !familyCode}
              >
                {isLoading ? (
                  <>
                    <div className="payFamilyFee-loading"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <FaSearch className="payFamilyFee-searchBtnIcon" />
                    Search Family
                  </>
                )}
              </button>
              <button className="payFamilyFee-forgotBtn" onClick={() => navigate("/Forget-Family-Code")}>
                <FaSearch className="payFamilyFee-forgotIcon" />
                Forgot Code?
              </button>
              <button
                className="payFamilyFee-refreshBtn"
                onClick={() => {
                  if (familyCode) handleSearch();
                }}
                disabled={!familyCode || isLoading}
              >
                <FaSync className="payFamilyFee-refreshIcon" />
                Refresh
              </button>
            </div>
          </div>
        </section>

        <FinancialIncompleteWarning />

        {renderContentBasedOnStatus()}

        {familyData && (
          <footer className="payFamilyFee-footer">
            <div className="payFamilyFee-footerContent">
              <div className="payFamilyFee-footerLeft">
                <div className="payFamilyFee-footerLogo">
                  <FaUsers className="payFamilyFee-footerIcon" />
                  Family Fee System
                </div>
                <p className="payFamilyFee-footerText">{getFooterText()}</p>
              </div>
              <div className="payFamilyFee-footerRight">
                <div className="payFamilyFee-systemStatus">
                  <div
                    className={`payFamilyFee-statusDot ${
                      isDbConnected ? "connected" : "disconnected"
                    }`}
                  ></div>
                  {isDbConnected ? "Database Connected" : "Database Disconnected"}
                </div>
                <div className="payFamilyFee-lastUpdated">
                  Last updated: {moment().format("DD MMM YYYY, hh:mm A")}
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default PayFamilyFee;